import { FC, useCallback, useMemo, useState } from "react"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import parseISO from "date-fns/parseISO/index.js"
import { formatDate } from "@/core/utils"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { DataGrid, SnackbarAlert } from "@core/components"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import {
  getRealizationState,
  setBatchesPagination,
  useBatchesListQuery,
  useDeleteBatchMutation,
} from "../../store"
import { Realization } from "../../interfaces"
import { BatchCouriersPanel } from "./batchCouriersPanel"

interface IProps {
  canWrite: boolean
}

export const BatchesTable: FC<IProps> = ({ canWrite }) => {
  const dispatch = useAppDispatch()
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const {
    batchesPagination: { page, pageSize },
    batchesFilters,
  } = useAppSelector(getRealizationState)

  const { data, isFetching } = useBatchesListQuery({
    queryParams: {
      page: page + 1,
      limit: pageSize,
      ...batchesFilters,
    },
  })

  const [deleteBatch] = useDeleteBatchMutation()

  const handleDelete = useCallback(
    async (params: GridRowParams<Realization.ProductBatch>) => {
      try {
        await deleteBatch({ pathParams: { batch_id: params.row.id } }).unwrap()
        setSuccessMsg("Партия удалена")
      } catch {
        setErrorMsg("Ошибка при удалении партии")
      }
    },
    [deleteBatch],
  )

  const columns: GridColDef<Realization.ProductBatch>[] = useMemo(
    () => [
      {
        headerName: "Номер партии",
        field: "batch_number",
        flex: 1.2,
      },
      {
        headerName: "Категория",
        field: "category",
        flex: 1.2,
        valueGetter: ({ row }) => row.category?.name ?? "—",
      },
      {
        headerName: "Курьеры",
        field: "couriers",
        flex: 1.4,
        // Backend may send `null`/omit the field for batches without
        // couriers instead of an empty array, so guard defensively here.
        valueGetter: ({ row }) =>
          row.couriers?.length
            ? row.couriers.map((c) => c.staff_name).join(", ")
            : "—",
      },
      {
        headerName: "Город",
        field: "city",
        flex: 1,
        valueGetter: ({ row }) => row.city?.name ?? "—",
      },
      {
        headerName: "Количество",
        field: "weight",
        flex: 0.8,
        valueGetter: ({ row }) => Number(row.weight),
      },
      {
        headerName: "Ед.",
        field: "measure",
        flex: 0.6,
        valueFormatter: ({ value }) => value ?? "—",
      },
      {
        headerName: "Дата партии",
        field: "batch_date",
        flex: 1,
        valueGetter: ({ row }) => parseISO(row.batch_date),
        valueFormatter: ({ value }) => formatDate(value),
        type: "dateTime",
      },
      {
        headerName: "Примечание",
        field: "note",
        flex: 1.5,
        valueFormatter: ({ value }) => value ?? "—",
      },
      {
        headerName: "Реализовано",
        field: "realized_weight",
        flex: 0.9,
        valueGetter: ({ row }) => Number(row.realized_weight),
      },
      {
        headerName: "Остаток",
        field: "remaining_weight",
        flex: 0.9,
        valueGetter: ({ row }) => Number(row.remaining_weight),
      },
      {
        headerName: "Создано",
        field: "created_at",
        flex: 1,
        valueGetter: ({ row }) => parseISO(row.created_at),
        valueFormatter: ({ value }) => formatDate(value),
        type: "dateTime",
      },
    ],
    [],
  )

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<Realization.ProductBatch>) => (
      <BatchCouriersPanel batch={row} canWrite={canWrite} />
    ),
    [canWrite],
  )
  const getDetailPanelHeight = useCallback(() => "auto" as const, [])

  const sharedProps = {
    columns,
    rows: data?.data ?? [],
    getRowId: (row: Realization.ProductBatch) => row.id,
    rowCount: data?.count ?? 0,
    loading: isFetching,
    filterMode: "client" as const,
    sortingMode: "client" as const,
    pagination: true as const,
    paginationMode: "server" as const,
    pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
    onPaginationModelChange: (model: { page: number; pageSize: number }) =>
      dispatch(setBatchesPagination(model)),
    initialState: {
      pagination: { paginationModel: { page, pageSize } },
    },
    getDetailPanelContent,
    getDetailPanelHeight,
  }

  return (
    <>
      {canWrite ? (
        <DataGrid<Realization.ProductBatch>
          {...sharedProps}
          canDelete
          onDelete={handleDelete}
        />
      ) : (
        <DataGrid<Realization.ProductBatch> {...sharedProps} />
      )}
      <SnackbarAlert
        message={successMsg ?? ""}
        isOpen={!!successMsg}
        variant="success"
        snackbarProps={{ onClose: () => setSuccessMsg(null) }}
      />
      <SnackbarAlert
        message={errorMsg ?? ""}
        isOpen={!!errorMsg}
        variant="error"
        snackbarProps={{ onClose: () => setErrorMsg(null) }}
      />
    </>
  )
}
